import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import { Dialog, DialogTitle, DialogContent, Typography, IconButton, Box, Divider, DialogActions, Button } from '@mui/material'
import { Close } from '@mui/icons-material'
import { omitBy } from 'lodash'
import moment from 'moment'
import { useGetEditPlanLogsForMerchantQuery } from 'src/_api/planApi'
import DataTable from 'src/components/common/DataTables/DataTable'

const $COLUMN_WIDTH = 200

export default function MerchantPlanEditsByAdminDialog({ merchantId, open, onClose }) {
    const { t } = useTranslation()
    const theme = useTheme()

    const [params, setParams] = useState({ page: 1, limit: 20 })

    const {
        data,
        isLoading,
        isFetching
    } = useGetEditPlanLogsForMerchantQuery({ ...omitBy(params, v => !v), merchant: merchantId }, { skip: (open && !!merchantId) ? false : true })

    const [rowCount, setRowCount] = useState(0)

    useEffect(() => {
        if (!!data) return setRowCount(data?.totalDocs)
    }, [data])

    const pageChangeHandler = (page) => {
        if (!isLoading && !isFetching) {
            setParams(prev => ({ ...prev, page }))
        }
    }

    const closeDialogHandler = () => {
        setParams({ page: 1, limit: 20 })
        setRowCount(0)
        onClose()
    }

    const columns = [
        {
            field: "plan",
            headerName: t("Plan"),
            sortable: false,
            width: $COLUMN_WIDTH * 1.2,
            valueGetter: ({ value }) => {
                const planName = theme.direction === "rtl" ? value?.name_ar : value?.name_en
                return planName || "-"
            }
        },
        {
            field: "prevExtraTaskPrice",
            headerName: t("Previous Extra Task Price"),
            sortable: false,
            width: $COLUMN_WIDTH * 1.1,
            valueGetter: ({ row }) => {
                return row?.plan?.extraTaskPrice ? `${row?.plan?.extraTaskPrice} ${t("Halalas")}` : "-"
            }
        },
        {
            field: "newExtraTaskPrice",
            headerName: t("New Extra Task Price"),
            sortable: false,
            width: $COLUMN_WIDTH * 1.1,
            valueGetter: ({ row }) => {
                return row?.newPlan?.extraTaskPrice ? `${row?.newPlan?.extraTaskPrice} ${t("Halalas")}` : "-"
            }
        },
        {
            field: "previousSMSPrice",
            headerName: t("Previous SMS Price"),
            sortable: false,
            width: $COLUMN_WIDTH * 1.1,
            valueGetter: ({ row }) => {
                return row?.plan?.SMSPrice ? `${row?.plan?.SMSPrice} ${t("Halalas")}` : "-"
            }
        },
        {
            field: "newSMSPrice",
            headerName: t("New SMS Price"),
            sortable: false,
            width: $COLUMN_WIDTH * 1.1,
            valueGetter: ({ row }) => {
                return row?.newPlan?.SMSPrice ? `${row?.newPlan?.SMSPrice} ${t("Halalas")}` : "-"
            }
        },
        {
            field: "admin",
            headerName: t("Admin"),
            sortable: false,
            width: $COLUMN_WIDTH * 1.2,
            valueGetter: ({ value }) => value?.name || "-"
        },
        {
            field: "createdAt",
            headerName: t("Created At"),
            sortable: false,
            width: $COLUMN_WIDTH * 1.1,
            valueGetter: ({ value }) => moment(value).format("DD/MM/YY - h:mm A")
        }
    ]

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { height: '80vh' }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5">
                    {t("Edits Logs")}
                </Typography>
                <IconButton onClick={onClose}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Box sx={{ height: "70vh" }}>
                    <DataTable
                        tableId="merchant-plan-edits-by-admin"
                        rows={data?.docs ?? []}
                        columns={columns}
                        loading={isLoading || isFetching}
                        rowCount={rowCount}
                        page={params?.page}
                        pageSize={20}
                        onRowClick={() => { }}
                        onPageChange={pageChangeHandler}
                        checkboxSelection={false}
                        customComponents={["Pagination"]}
                    />
                </Box>
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button
                    variant="contained"
                    onClick={closeDialogHandler}
                    sx={{ textTransform: 'none' }}
                >
                    {t("Close")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
